import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {
  GetPaginatedProductsGQL,
  GetPaginatedProductsQuery,
  GetProductGQL,
  PaginationMode,
  ProductFragment,
} from '../../../generated/graphql';
import {Product} from "../../models/product";
import {DoorType} from "../../models/door";

export interface CursorBasedPaginationInfo {
  hasNextPage: boolean;
  hasPreviousPage?: boolean;
  startCursor?: string;
  endCursor?: string;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalPages: number;
  totalResults: number;
}

export interface PaginationInformation {
  paginationMode: PaginationMode,
  paginationInfo?: PaginationInfo,
  cursorBasedPaginationInfo?: CursorBasedPaginationInfo
}

export interface ProductFilter {
  productType?: string;
  visualDoorId?: string;
  customerKey: string;
}

@Injectable({providedIn: 'root'})
export class ProductLoader {
  constructor(private readonly getPaginatedProductsGQL: GetPaginatedProductsGQL,
              private readonly getProductGQL: GetProductGQL) {
  }

  private initialProductLoadCount = 12 * 2.5

  public getProductsPaginated(filter: ProductFilter, paginationArgs: {
    page?: number,
    pageSize?: number,
    afterCursor?: string,
    mode: PaginationMode
  }):
    Observable<{ result: Array<Product> } & PaginationInformation> {
    return this.getPaginatedProductsGQL.fetch({
      pagination: {
        mode: paginationArgs.mode,
        page: paginationArgs.page,
        afterCursor: paginationArgs.afterCursor,
        pageSize: paginationArgs.pageSize || this.initialProductLoadCount,
      },
      filter,
    }, {
      fetchPolicy: 'no-cache'
    }).pipe(map(result => {

      const data= result.data.getPaginatedProducts

      if (!data.results) {
        return {
          totalPages: 0,
          paginationMode: PaginationMode.Page,
          result: []
        };
      }

      return {
        paginationMode: PaginationMode.Cursor,
        cursorBasedPaginationInfo: this.mapCursorBasedPaginationInfo(data.cursorBasedPaginationInfo),
        result: data.results.filter(Boolean).map(product => {
          return this.mapProduct(product);
        }) as Array<Product>
      }
    }))
  }

  private mapCursorBasedPaginationInfo(graphQlPageInfo?: GetPaginatedProductsQuery['getPaginatedProducts']['cursorBasedPaginationInfo']): CursorBasedPaginationInfo | undefined {
    if (!graphQlPageInfo) {
      return undefined
    }
    return {
      endCursor: graphQlPageInfo.endCursor || undefined,
      hasNextPage: graphQlPageInfo.hasNextPage,
      hasPreviousPage: graphQlPageInfo?.hasPreviousPage || undefined,
      startCursor: graphQlPageInfo?.startCursor || undefined,
    }
  }

  private mapProduct(product: ProductFragment | undefined): Product | undefined {
    if (!product) {
      return undefined;
    }
    return {
      id: product.id,
      externalIds: product.externalIds,
      productType: product.productType,
      visualisationImageUrl: product.visualisationImage?.url,
      thumbnailUrl: product.thumbnailImage?.url,
      customData: product.customData || undefined,
      name: product.name,
      price: product.price || undefined,
      link: product.link || undefined,
      enabled: product.enabled,
      visualisationLayer: product.visualisationLayer || undefined,
      renderingContextFilter: product.renderingContextFilter || undefined,
      renderingContextFillStyle: product.renderingContextFillStyle || undefined,
      isFavorite: false,
      // TODO this is only here to satisfy the interface. Find a way to avoid that.
      // But the product interface is now also used for doors and there it is mandatory
      doorType: DoorType.SINGLE
    };
  }

  public getProduct(customerKey: string, externalId: string): Observable<Product | undefined> {
    return this.getProductGQL.fetch({
      customerKey,
      externalId
    }, {
      context: {
        ignoreDefaultErrorHandling: true
      },
      fetchPolicy: 'no-cache',
    }).pipe(map(result => {
      return this.mapProduct(result.data.getProductByExternaldId || undefined)
    }));
  }
}
